import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button } from "@material-ui/core";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, {
  EditorFieldControl,
  TextFieldControl,
} from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";

import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  compose,
  fileSizeValidator,
  imageIsCorruptedValidator,
  imageTypesValidator,
  lengthValidator,
} from "utils/validators";

import { PUBLISH_STATUSES } from "constants/models";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  Divider,
  UploadPreviewFieldControl,
  ImagePreviewContainer,
  PublishStatusWrapper,
} from "./styles";
import CoachStore from "stores/CoachStore";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ coachId: string }, {}, LocationState> &
  SnackbarContextProps & {};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class CoachDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewCoach() {
    const { coachId } = this.props.match.params;

    return coachId === undefined;
  }

  get coachId() {
    const { coachId } = this.props.match.params;

    const parsedCoachId = Number.parseInt(coachId);
    return typeof parsedCoachId === "number" ? parsedCoachId : undefined;
  }

  get backClickInfo() {
    return " Back to Coaches";
  }

  get backClickPath() {
    return ROUTES.CONTENT_PERSONAL_COACH;
  }

  @computed get displayValues(): any {
    const formData = CoachStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get initialValues() {
    if (this.isNewCoach) {
      return {};
    } else {
      return {
        ...CoachStore.details?.formData,
      };
    }
  }

  @computed get coachPublishStatus() {
    return CoachStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    if (this.isNewCoach) {
      history.replace({ state: { editMode: true } });
    } else {
      await CoachStore.fetchCoachDetails(this.coachId);
    }
  }

  componentWillUnmount() {
    CoachStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewCoach) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_PERSONAL_COACH_DETAIL, {
      coachId: this.coachId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_PERSONAL_COACH_DETAIL, {
      coachId: this.coachId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.coachPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.coachPublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const {
      file,
      id,
      title,
      description,
      email,
      fasterwayCertifications,
      professionalCertifications,
      availableHours,
    } = values;

    const data = {
      title: title?.trim(),
      email: email?.trim(),
      description: description?.trim(),
      fasterwayCertifications: fasterwayCertifications?.trim(),
      professionalCertifications: professionalCertifications?.trim(),
      availableHours: availableHours?.trim(),
    };

    try {
      if (this.isNewCoach) {
        const id = await CoachStore.createCoach(data);

        if (id) {
          if (file) {
            if (typeof file !== "string") {
              const payload = { file, id };
              await CoachStore.editCoachPhoto(payload);
            }
          }

          const path = generatePath(ROUTES.CONTENT_PERSONAL_COACH_DETAIL, {
            coachId: id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          CoachStore.fetchCoachDetails(id);
        }
      } else {
        const payload = { ...data, id };
        await CoachStore.editCoachInfo(payload);

        if (file) {
          if (typeof file !== "string") {
            const payload = { file, id };
            await CoachStore.editCoachPhoto(payload);
          }
        }

        this.exitEditMode();
        CoachStore.fetchCoachDetails(id);
      }
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Coach Details"
        leftControls={
          this.coachPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.coachPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickInfo}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={CoachStore.isCoachLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewCoach}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{
              dirty: true,
              dirtyFields: true,
              initialValues: true,
              values: true,
            }}
            onChange={debounce(this.onChangeIsPopupDirty, 500)}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column width="50%">
                <Row>
                  {this.isEditMode ? (
                    <UploadPreviewFieldControl
                      variant="outlined"
                      margin="none"
                      name="file"
                      acceptedFiles={ACCEPTED_IMAGE_TYPES}
                      validate={compose(
                        this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                        fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                        imageIsCorruptedValidator,
                      )}
                    />
                  ) : (
                    <ImagePreviewContainer image={this.displayValues?.photo} />
                  )}
                </Row>
              </Column>

              <Column>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Name"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Name"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="email"
                        label="Email"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Email"
                        value={this.displayValues?.email}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="professionalCertifications"
                        label="Professional Certifications"
                        fullWidth
                      />
                    ) : (
                      <SimpleField
                        label="Professional Certifications"
                        value={this.displayValues?.professionalCertifications}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="fasterwayCertifications"
                        label="FASTer Way Certifications"
                        fullWidth
                      />
                    ) : (
                      <SimpleField
                        label="FASTer Way Certifications"
                        value={this.displayValues?.fasterwayCertifications}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="availableHours"
                        label="Available Hours"
                        fullWidth
                      />
                    ) : (
                      <SimpleField
                        label="Available Hours"
                        value={this.displayValues?.availableHours}
                      />
                    )}
                  </Column>
                </Row>
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                <Row>
                  {this.isEditMode ? (
                    <EditorFieldControl
                      editorRef={this.quillEditorRef}
                      name="description"
                      title="Description"
                      theme="snow"
                      changeDebounce={0}
                    />
                  ) : (
                    <SimpleField
                      label="Description"
                      value={this.displayValues?.description}
                      isRichText
                    />
                  )}
                </Row>
              </Column>
            </GroupRow>
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={CoachStore.isCoachManaging}
      fullWidth
    >
      {this.isNewCoach ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(CoachDetails));
